import { ReactElement } from 'react';
import { RunHealthStatus, SlipSlopeHealth } from 'types/Run';
import { sensorPositionLabel } from '../../CalibrationView/groundSpeedUtils';

interface SlipSlopeLineProps {
  slip: SlipSlopeHealth | null | undefined;
  /** The ANT id of the speed card this line sits on. */
  sensorId: number | null | undefined;
}

function dotClass(status: number | null): string {
  if (status === RunHealthStatus.GOOD) return 'cr-bg-green-500';
  if (status === RunHealthStatus.WARNING) return 'cr-bg-orange-500';
  return 'cr-bg-gray-300';
}

/**
 * The two-TEMPO wheel-position check, on each of the two speed cards it covers.
 * The rear tyre turns slightly faster than the front under acceleration, so the
 * sign of the speed ratio's slope against acceleration says which wheel is which.
 * Green: the slope matches the positions. Orange: it says they are swapped.
 */
export default function SlipSlopeLine({ slip, sensorId }: SlipSlopeLineProps): ReactElement | null {
  if (!slip || sensorId == null) return null;
  const isPrimary = sensorId === slip.primary_id;
  if (!isPrimary && sensorId !== slip.secondary_id) return null;

  const own = sensorPositionLabel((isPrimary ? slip.primary_position : slip.secondary_position) ?? undefined);
  const other = sensorPositionLabel((isPrimary ? slip.secondary_position : slip.primary_position) ?? undefined);
  const fit = slip.t_value != null ? ` (t = ${slip.t_value.toFixed(1)}, ${slip.n_samples} samples)` : '';
  let text: string;
  switch (slip.verdict) {
    case 'agrees':
      text = `Wheel position confirmed: slips like the ${own?.toLowerCase()} wheel${fit}`;
      break;
    case 'disagrees':
      text = `Set as ${own?.toLowerCase()}, but slips like the ${other?.toLowerCase()} wheel${fit}. `
        + 'Check which wheel each sensor is on.';
      break;
    case 'inconclusive':
      text = `Wheel position not confirmed: ${slip.note ?? 'no clear slope'}${fit}`;
      break;
    default:
      text = `Wheel position not checked: ${slip.note ?? ''}`;
  }

  return (
    <div className="cr-mt-2 cr-border-t cr-border-dashed cr-pt-2">
      <div className="cr-font-semibold cr-mb-1">Slip vs Acceleration</div>
      <div className="cr-flex cr-items-start cr-gap-2">
        <span className={`cr-mt-1 cr-inline-block cr-h-2 cr-w-2 cr-shrink-0 cr-rounded-full ${dotClass(slip.status)}`} />
        <span>{text}</span>
      </div>
    </div>
  );
}
